import { matchesWorkflowStepStatus, workflowTreePathKey } from "./workflow-step-filter.ts";

import type { RunDetailTree, StepSummary } from "./workflows-api";

export const WORKFLOW_TREE_CHILD_LOADING_CLASS =
  "flex w-full items-center gap-2 border-t border-[var(--border-soft)] bg-[var(--card-2)] px-3 py-2 text-[11px] text-[var(--nzr-muted)]";

export function workflowTreeChildLoadingPadding(depth: number): string {
  const safeDepth = Number.isFinite(depth) ? Math.max(0, Math.floor(depth)) : 0;
  return `calc(var(--tl) + ${(safeDepth + 2) * 14 + 4}px)`;
}

export function workflowTreeRootSteps(tree: RunDetailTree | undefined): StepSummary[] {
  if (!tree) return [];
  return tree.roots.flatMap((root) => {
    const nodeKey = workflowTreePathKey([root.key]);
    const placeholder: StepSummary = {
        stepId: `tree-root:${root.key}`,
        nodeKey,
        parentKey: null,
        hasChildren: root.childrenHasMore,
        isPlaceholder: true,
        stepName: root.key,
        stepShortName: root.label,
        status: root.status,
        attempt: 0,
        createdAt: null,
        startedAt: null,
        updatedAt: null,
        input: null,
        output: null,
        error: null,
        defaultCollapsed: true,
        tree: [{ key: root.key, label: root.label, collapsed: true }],
        progress: root.progress ?? null,
      };
    const rootSteps = (root.rootSteps ?? []).map((step) => ({
      ...step,
      nodeKey: step.nodeKey ?? nodeKey,
      parentKey: step.parentKey ?? null,
      hasChildren: step.hasChildren === true || root.childrenHasMore,
      status: root.status,
      progress: root.progress ?? step.progress ?? null,
    }));
    return [placeholder, ...rootSteps, ...root.children];
  });
}

export function workflowTreeNodeKey(step: StepSummary): string {
  if (step.nodeKey) return step.nodeKey;
  const path = step.tree?.map((item) => item.key) ?? [];
  return workflowTreePathKey(path.length > 0 ? path : [step.stepShortName]);
}

export function mergeWorkflowTreeSteps(steps: readonly StepSummary[]): StepSummary[] {
  const selected = new Map<string, StepSummary>();
  const order: string[] = [];
  const actualNodeKeys = new Set<string>();
  const timestamp = (step: StepSummary) => {
    const value = step.updatedAt ?? step.createdAt;
    const parsed = value ? Date.parse(value) : Number.NEGATIVE_INFINITY;
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  for (const step of steps) {
    const nodeKey = workflowTreeNodeKey(step);
    const key = step.isPlaceholder ? `placeholder:${nodeKey}` : `step:${step.stepId}`;
    if (!step.isPlaceholder) {
      actualNodeKeys.add(nodeKey);
      const placeholderKey = `placeholder:${nodeKey}`;
      if (selected.delete(placeholderKey)) {
        const placeholderIndex = order.indexOf(placeholderKey);
        if (0 <= placeholderIndex) order.splice(placeholderIndex, 1);
      }
    } else if (actualNodeKeys.has(nodeKey)) {
      continue;
    }
    const current = selected.get(key);
    if (!current) {
      selected.set(key, step);
      order.push(key);
    } else if (timestamp(step) >= timestamp(current)) {
      selected.set(key, step);
    }
  }
  return order.map((key) => selected.get(key) as StepSummary);
}

/**
 * Returns only branch identities that are both expandable and visible. A
 * collapsed branch therefore cannot keep a child request alive in the
 * background.
 */
export function workflowVisibleBranchKeys(
  steps: readonly StepSummary[],
  isExpanded: (key: string) => boolean,
): string[] {
  const keys = new Set<string>();
  for (const step of steps) {
    const path = step.tree?.map((item) => item.key) ?? [];
    const pathKeys = path.map((_item, index) => workflowTreePathKey(path.slice(0, index + 1)));
    const branchKey = pathKeys.at(-1) ?? workflowTreeNodeKey(step);
    const ancestorsExpanded = pathKeys.every((key) => isExpanded(key));
    if (step.hasChildren && ancestorsExpanded) {
      keys.add(branchKey);
    }
  }
  return [...keys].sort();
}

export function mergeWorkflowTreePages(
  rootSteps: readonly StepSummary[],
  branchPages: readonly (readonly StepSummary[])[],
): StepSummary[] {
  return mergeWorkflowTreeSteps([
    ...rootSteps,
    ...branchPages.flat(),
  ]);
}

/**
 * Keep the tree navigable while filtering physical leaf rows by status.
 * Root and expandable rows provide the context needed to reach matching
 * descendants even when their own status does not match the URL filter.
 */
export function filterWorkflowTreeStepsByStatus(
  steps: readonly StepSummary[],
  runStatus: string,
  statuses: readonly string[],
): StepSummary[] {
  if (statuses.length === 0) return [...steps];
  const matchingPaths = steps
    .filter((step) => matchesWorkflowStepStatus(step.status, runStatus, statuses))
    .map((step) => step.tree?.map((item) => item.key) ?? []);
  return steps.filter((step) => {
    const pathLength = step.tree?.length ?? 0;
    const path = step.tree?.map((item) => item.key) ?? [];
    const hasMatchingDescendant = matchingPaths.some((matchingPath) =>
      matchingPath.length > path.length && path.every((key, index) => matchingPath[index] === key));
    const isRootContext = (step.isPlaceholder || step.parentKey === null || pathLength <= 1)
      && hasMatchingDescendant;
    const isExpandableContext = step.hasChildren === true && hasMatchingDescendant;
    return isRootContext
      || isExpandableContext
      || matchesWorkflowStepStatus(step.status, runStatus, statuses);
  });
}
